import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  where,
  startAfter,
} from "../firebase";
import { filterUndefinedValues, uniqueVals } from "@markit/common.utils";
import {
  AccountData,
  OrganizerType,
  RequestStatus,
  TicketV2,
  Event,
  FollowerStatus,
} from "@markit/common.types";
import { getTicketsRef } from "../utils/FirebaseUtils";
import { loadAlgoliaUsersSearchResults } from "../utils/algoliaUtils";
import { useDispatch, useSelector } from "react-redux";
import { accountActions, getAccountState } from "../redux/slices/accountSlice";
import { fetchSingleFollower, fetchSingleUser } from "../utils/FetchSingleData";
import useOnUnmount from "./useOnUnmount";

type useLoadUserTicketListProps = {
  event?: Event;
  followerStatus?: FollowerStatus;
  windowSize: number;
  ticketType: string;
  /** Preloaded user data for all event tickets; hook only fetches for live new data not in this set */
  preloadedUserData?: AccountData[];
};
export const useLoadUserTicketList = (props: useLoadUserTicketListProps) => {
  const { event, followerStatus, windowSize, ticketType, preloadedUserData } =
    props;

  const { followingAccountData, followersData } =
    useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [fetchedUserData, setFetchedUserData] = useState<AccountData[]>([]);
  const [fetchedTicketData, setFetchedTicketData] = useState<TicketV2[]>([]);
  const [fetchedOldestTicketData, setFetchedOldestTicketData] = useState<
    TicketV2[]
  >([]);
  const [fetchedOrderedTicketData, setFetchedOrderedTicketData] = useState<
    TicketV2[]
  >([]);
  const [fetchedOrderedTicketMap, setFetchedOrderedTicketMap] = useState(
    new Map<TicketV2, string>()
  );
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [lastVisible, setLastVisible] = useState<string>(
    new Date().toISOString()
  );
  const [loadedAllMessages, setLoadedAllMessages] = useState(false);

  useOnUnmount(
    (latestDeps) => {
      dispatch(
        accountActions.addMultipleFollowingAccountData(
          latestDeps[0] as AccountData[]
        )
      );
      unsubscribeArrayRef.current.forEach(
        (unsubscribe) => unsubscribe && unsubscribe()
      );
      unsubscribeArrayRef.current = []; // Clear the array after cleanup
    },
    [fetchedUserData]
  );

  const addUserData = useCallback((newUserData: AccountData[]) => {
    setFetchedUserData((prevData) => {
      const userMap = new Map(prevData.map((user) => [user.uid, user]));
      newUserData.forEach((user) => userMap.set(user.uid, user));
      return Array.from(userMap.values());
    });
  }, []);

  // Merge preloaded user data into cache so we only fetch for live new data
  useEffect(() => {
    if (preloadedUserData?.length) {
      addUserData(preloadedUserData);
    }
  }, [preloadedUserData, addUserData]);

  const addTicketData = useCallback(
    (newTicketData: TicketV2[]) => {
      setFetchedTicketData((prevData) => {
        const ticketMap = new Map(prevData.map((ticket) => [ticket.id, ticket]));
        newTicketData.forEach((ticket) => ticketMap.set(ticket.id, ticket));
        return Array.from(ticketMap.values()).sort((a, b) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ticketType === "organizers" ? ta - tb : tb - ta;
        });
      });
    },
    [ticketType]
  );

  const removeTicketData = useCallback((ticketData: TicketV2) => {
    setFetchedTicketData((fetchedTicketData) => {
      const index = fetchedTicketData.findIndex(
        (fetched) => fetched.id === ticketData.id
      );
      if (index !== -1) {
        fetchedTicketData.splice(index, 1);
      }
      return [...fetchedTicketData];
    });
  }, []);

  const addOldestTicketData = useCallback((newTicketData: TicketV2[]) => {
    setFetchedOldestTicketData((fetchedOldestTicketData) =>
      uniqueVals(
        fetchedOldestTicketData.concat(newTicketData),
        (ticketData) => ticketData.id
      )
    );
  }, []);

  const removeOldestTicketData = useCallback((ticketData: TicketV2) => {
    setFetchedOldestTicketData((fetchedOldestTicketData) => {
      const index = fetchedOldestTicketData.findIndex(
        (fetched) => fetched.id === ticketData.id
      );
      if (index !== -1) {
        fetchedOldestTicketData.splice(index, 1);
      }
      return fetchedOldestTicketData;
    });
  }, []);

  // TODO (jonathan): this still uses old logic, but doesn't really matter, so not refactoring to account for fullName sorting
  const addOrderedTicketData = useCallback(
    (newTicketData: TicketV2, fullName: string) => {
      setFetchedOrderedTicketData((fetchedOrderedTicketData) => {
        const index = fetchedOrderedTicketData.findIndex(
          (fetched) => fetched.id === newTicketData.id
        );
        if (index !== -1) {
          fetchedOrderedTicketData.splice(index, 1, newTicketData);
          return uniqueVals(
            fetchedOrderedTicketData,
            (fetchedOrderedTicketData) => fetchedOrderedTicketData.id
          ).sort();
        } else {
          const newTicketMap = fetchedOrderedTicketMap.set(
            newTicketData,
            fullName
          );
          setFetchedOrderedTicketMap(newTicketMap);
          return Array.from(
            new Map(Array.from(newTicketMap.entries()).sort()).keys()
          );
        }
      });
    },
    [fetchedOrderedTicketMap]
  );

  const removeOrderedTicketData = useCallback(
    (newTicketData: TicketV2) => {
      setFetchedOrderedTicketData((fetchedOrderedTicketData) => {
        const index = fetchedOrderedTicketData.findIndex(
          (fetched) => fetched.id === newTicketData.id
        );
        if (index !== -1) {
          fetchedOrderedTicketMap.delete(newTicketData);
          setFetchedOrderedTicketMap(fetchedOrderedTicketMap);
          return Array.from(
            new Map(Array.from(fetchedOrderedTicketMap.entries()).sort()).keys()
          );
        }

        return uniqueVals(
          fetchedOrderedTicketData,
          (fetchedOrderedTicketData) => fetchedOrderedTicketData.id
        );
      });
    },
    [fetchedOrderedTicketMap]
  );

  const isFinished = useMemo(() => loadedAllMessages, [loadedAllMessages]);

  const updateData = useCallback(
    async (foundTicketData: TicketV2[]) => {
      if (event) {
        const userCache = new Map(
          [
            ...(preloadedUserData ?? []),
            ...fetchedUserData,
          ].map((u) => [u.uid, u])
        );

        const membersData = await Promise.all(
          foundTicketData.map(async (ticketData) => {
            if (
              ticketType === "organizers" ||
              (ticketData &&
                ticketData.customTicketId !== "" &&
                ticketData.uid !== "")
            ) {
              const needsUserData =
                (ticketType === "organizers" && ticketData.uid !== "") ||
                (ticketType !== "organizers" && ticketData.redeemedBy !== "");
              const cachedUser = ticketData.uid
                ? userCache.get(ticketData.uid)
                : undefined;

              if (needsUserData) {
                let userData = cachedUser;
                if (!userData && ticketData.uid) {
                  userData = await fetchSingleUser(
                    ticketData.uid,
                    followingAccountData
                  );
                  if (followerStatus) {
                    const followerData = await fetchSingleFollower(
                      event.createdBy,
                      ticketData.uid,
                      followersData
                    );
                    userData =
                      userData &&
                      followerData &&
                      followerStatus === followerData.status
                        ? userData
                        : undefined;
                  }
                }
                return {
                  membersTicketData: ticketData,
                  membersUserData: userData,
                };
              }
              return {
                membersTicketData: ticketData,
                membersUserData: undefined,
              };
            }
            return undefined;
          })
        );

        const definedMembersData: {
          membersTicketData: TicketV2;
          membersUserData: AccountData | undefined;
        }[] = filterUndefinedValues(membersData);

        const followerStatusMembersTicketData: (TicketV2 | undefined)[] =
          definedMembersData.map((member) => member.membersTicketData);
        const followerStatusMembersUserData: (AccountData | undefined)[] =
          definedMembersData.map((member) => member.membersUserData);

        addTicketData(filterUndefinedValues(followerStatusMembersTicketData));
        addUserData(filterUndefinedValues(followerStatusMembersUserData));
      }
    },
    [
      addTicketData,
      addUserData,
      event,
      fetchedUserData,
      followerStatus,
      followersData,
      followingAccountData,
      preloadedUserData,
      ticketType,
    ]
  );

  const initialUpdateData = useCallback(
    async (foundTicketData: TicketV2[]) => {
      if (foundTicketData.length) {
        setLastVisible(foundTicketData[foundTicketData.length - 1].createdAt);
      }
      if (foundTicketData.length < windowSize) {
        setLoadedAllMessages(true);
      }

      if (foundTicketData.length > 0) {
        await updateData(foundTicketData);
      }
    },
    [updateData, windowSize]
  );

  const liveUpdateData = useCallback(
    async (
      type: "added" | "modified" | "removed",
      ticketData: TicketV2,
      foundTicketData: TicketV2[]
    ) => {
      if (
        (type === "added" &&
          !foundTicketData.some((found) => found.id === ticketData.id)) ||
        type === "modified"
      ) {
        updateData([ticketData]);
      } else if (type === "removed") {
        removeTicketData(ticketData);
      }
    },
    [removeTicketData, updateData]
  );

  const loadTicketUsers = useCallback(async () => {
    if (!isFinished && !isLoading) {
      if (!event) {
        setFetchedUserData([]);
        setFetchedTicketData([]);
        return;
      }
      setIsLoading(true);

      const ticketsRef = getTicketsRef(event.id);
      const ticketUsersQuery =
        ticketType === "organizers"
          ? query(
              ticketsRef,
              where("customTicketId", "==", ""),
              orderBy("createdAt", "asc"),
              limit(windowSize)
            )
          : ticketType !== ""
          ? query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("requestStatus", "==", ticketType),
              orderBy("createdAt", "desc"),
              limit(windowSize)
            )
          : query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("requestStatus", "!=", RequestStatus.REFUNDED),
              orderBy("requestStatus", "desc"),
              orderBy("createdAt", "desc"),
              limit(windowSize)
            );

      const foundTicketUsers = (await getDocs(ticketUsersQuery)).docs.map(
        (doc) => doc.data()
      );
      await initialUpdateData(foundTicketUsers);

      const unsubscribe = onSnapshot(ticketUsersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message, index) => {
          const ticketData = message.doc.data();
          liveUpdateData(message.type, ticketData, foundTicketUsers);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);

      setIsLoading(false);
    }
  }, [
    event,
    initialUpdateData,
    isFinished,
    isLoading,
    liveUpdateData,
    ticketType,
    windowSize,
  ]);

  const loadMoreTicketUsers = useCallback(async () => {
    if (!isFinished) {
      if (!event) {
        setFetchedUserData([]);
        setFetchedTicketData([]);
        return;
      }
      setIsLoading(true);
      const ticketsRef = getTicketsRef(event.id);
      const ticketUsersQuery =
        ticketType === "organizers"
          ? query(
              ticketsRef,
              where("customTicketId", "==", ""),
              orderBy("createdAt", "asc"),
              startAfter(lastVisible),
              limit(windowSize)
            )
          : ticketType !== ""
          ? query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("requestStatus", "==", ticketType),
              orderBy("createdAt", "desc"),
              startAfter(lastVisible),
              limit(windowSize)
            )
          : query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("requestStatus", "!=", RequestStatus.REFUNDED),
              orderBy("requestStatus", "desc"),
              orderBy("createdAt", "desc"),
              startAfter(lastVisible),
              limit(windowSize)
            );

      const foundTicketUsers = (await getDocs(ticketUsersQuery)).docs.map(
        (doc) => doc.data()
      );
      await initialUpdateData(foundTicketUsers);

      const unsubscribe = onSnapshot(ticketUsersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message, index) => {
          const ticketData = message.doc.data();
          liveUpdateData(message.type, ticketData, foundTicketUsers);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);

      setIsLoading(false);
    }
  }, [
    event,
    initialUpdateData,
    isFinished,
    lastVisible,
    liveUpdateData,
    ticketType,
    windowSize,
  ]);

  /*
   * Start searching utils
   */
  // need to return account data for ordered user data case
  const checkIfAddUserData = useCallback(
    async (uid: string): Promise<AccountData | undefined> => {
      const userData = await fetchSingleUser(uid, followingAccountData);
      if (event && userData) {
        if (followerStatus) {
          const followerData = await fetchSingleFollower(
            event.createdBy,
            userData.uid,
            followersData
          );
          if (followerData) {
            if (followerData.status === followerStatus) {
              addUserData([userData]);
              return userData;
            }
          }
        } else {
          addUserData([userData]);
          return userData;
        }
      }
    },
    [addUserData, event, followerStatus, followersData, followingAccountData]
  );

  // Subscribes to this uid's ticket(s) directly (targeted query), so a
  // specific person's ticket is fetched even if it hasn't been paginated into
  // fetchedTicketData yet. Used by name search (fanned out over the
  // Algolia-resolved uid list) and by phone search (a single resolved uid).
  const loadTicketsForUid = useCallback(
    (uid: string) => {
      if (!event) {
        return;
      }
      const ticketsRef = getTicketsRef(event.id);
      const query_ =
        ticketType !== ""
          ? query(
              ticketsRef,
              where("requestStatus", "==", ticketType),
              where("uid", "==", uid)
            )
          : query(
              ticketsRef,
              where("uid", "==", uid),
              where("requestStatus", "!=", RequestStatus.REFUNDED)
            );

      const unsubscribe = onSnapshot(query_, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          if (message.type === "added" || message.type === "modified") {
            const ticketUserData = message.doc.data();
            if (
              ticketUserData &&
              ((ticketUserData.customTicketId !== "" &&
                ticketUserData.uid !== "") ||
                ticketType === "organizers")
            ) {
              addTicketData([ticketUserData]);
              if (
                ticketType === "organizers" ||
                ticketUserData.requestStatus === RequestStatus.PENDING
                  ? ticketUserData.uid !== ""
                  : ticketUserData.redeemedBy !== "" &&
                    ticketUserData.redeemedBy !== "manual redeemed" &&
                    ticketUserData.redeemedBy !== "manual redeemed scanned"
              ) {
                checkIfAddUserData(
                  ticketUserData.requestStatus === RequestStatus.PENDING
                    ? ticketUserData.uid
                    : ticketUserData.redeemedBy
                );
              }
            }
          } else if (message.type === "removed") {
            const ticketUserData = message.doc.data();
            removeTicketData(ticketUserData);
          }
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);
      return unsubscribe;
    },
    [addTicketData, checkIfAddUserData, event, removeTicketData, ticketType]
  );

  const loadSearchResults = useCallback(
    async (searchTerm: string) => {
      if (!event) {
        setFetchedUserData([]);
        setFetchedTicketData([]);
      } else {
        const fetchEventUsers = async (userIds: string[]) => {
          setIsLoading(true);
          userIds.forEach((uid) => loadTicketsForUid(uid));
          setIsLoading(false);
        };
        await loadAlgoliaUsersSearchResults(
          searchTerm,
          `eventIds:${event.id}`,
          (userIds: string[]) => fetchEventUsers(userIds)
        );
      }
    },
    [event, loadTicketsForUid]
  );

  const loadSearchResultsThrottled = useCallback(
    (searchTerm: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (searchTerm.length > 1) {
          loadSearchResults(searchTerm);
        }
      }, 500);
    },
    [loadSearchResults]
  );
  /*
   * End searching utils
   */

  // used to sort the list alphabetically. Instead of incremental fetching, need
  // to fetch the whole data set in this case.
  const loadAlphabeticalTicketUsers = useCallback(async () => {
    if (!isFinished && !isLoading) {
      if (!event) {
        setFetchedUserData([]);
        setFetchedTicketData([]);
        return;
      }
      setIsLoading(true);
      const ticketsRef = getTicketsRef(event.id);
      const ticketUsersQuery =
        ticketType === "organizers"
          ? query(
              ticketsRef,
              where("customTicketId", "==", ""),
              where("redeemedBy", "!=", "")
            )
          : query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("uid", "!=", ""),
              where("requestStatus", "==", ticketType)
            );
      const unsubscribe = onSnapshot(ticketUsersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message, index) => {
          if (message.type === "added" || message.type === "modified") {
            const ticketUserData = message.doc.data();
            if (ticketType === "organizers") {
              if (ticketUserData.redeemedBy !== "") {
                const userData = await checkIfAddUserData(ticketUserData.uid);
                if (userData) {
                  addOrderedTicketData(ticketUserData, userData.fullName);
                }
              }
            } else {
              if (
                ticketUserData &&
                ticketUserData.customTicketId !== "" &&
                ticketUserData.uid !== ""
              ) {
                // need to add ticket data after fetching user data here so we can sort by full name
                // Also different conditional than on rest because we need to always return userData
                // so we can return the full name to apply to the ordered ticket data
                const userData = await checkIfAddUserData(ticketUserData.uid);
                if (userData) {
                  addOrderedTicketData(ticketUserData, userData.fullName);
                }
              }
            }
          } else if (message.type === "removed") {
            const ticketUserData = message.doc.data();
            removeOrderedTicketData(ticketUserData);
          }
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);

      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      return unsubscribe;
    }
  }, [
    addOrderedTicketData,
    checkIfAddUserData,
    event,
    isFinished,
    isLoading,
    removeOrderedTicketData,
    ticketType,
  ]);

  // used to sort the list alphabetically. Instead of incremental fetching, need
  // to fetch the whole data set in this case.
  const loadByOldestTicketUsers = useCallback(async () => {
    if (!isFinished && !isLoading) {
      if (!event) {
        setFetchedUserData([]);
        setFetchedTicketData([]);
        return;
      }
      setIsLoading(true);
      const ticketsRef = getTicketsRef(event.id);
      const ticketUsersQuery =
        ticketType === "organizers"
          ? query(
              ticketsRef,
              where("customTicketId", "==", ""),
              orderBy("createdAt", "desc")
            )
          : query(
              ticketsRef,
              where("role.type", "==", OrganizerType.ATTENDEE),
              where("requestStatus", "==", ticketType),
              orderBy("createdAt", "asc")
            );

      const unsubscribeOldest = onSnapshot(ticketUsersQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message, index) => {
          if (message.type === "added" || message.type === "modified") {
            const ticketUserData = message.doc.data();
            if (ticketType === "organizers") {
              addOldestTicketData([ticketUserData]);
              if (ticketUserData.uid !== "") {
                addTicketData([ticketUserData]);
                if (ticketUserData.redeemedBy !== "") {
                  checkIfAddUserData(ticketUserData.uid);
                }
              }
            } else {
              if (
                ticketUserData &&
                ticketUserData.customTicketId !== "" &&
                ticketUserData.uid !== ""
              ) {
                addOldestTicketData([ticketUserData]);

                if (ticketUserData.redeemedBy !== "") {
                  checkIfAddUserData(ticketUserData.uid);
                } else if (
                  !fetchedUserData.find(
                    (fetched) => fetched.uid === ticketUserData.uid
                  )
                ) {
                  checkIfAddUserData(ticketUserData.uid);
                }
              }
            }
          } else if (message.type === "removed") {
            const ticketUserData = message.doc.data();
            removeOldestTicketData(ticketUserData);
          }
        });
      });
      unsubscribeArrayRef.current.push(unsubscribeOldest);

      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      return unsubscribeOldest;
    }
  }, [
    addOldestTicketData,
    addTicketData,
    checkIfAddUserData,
    event,
    fetchedUserData,
    isFinished,
    isLoading,
    removeOldestTicketData,
    ticketType,
  ]);

  return {
    isFinished,
    isLoading,
    fetchedUserData,
    fetchedTicketData,
    fetchedOldestTicketData,
    fetchedOrderedTicketData,
    loadTicketUsers,
    loadMoreTicketUsers,
    loadSearchResultsThrottled,
    loadAlphabeticalTicketUsers,
    loadByOldestTicketUsers,
    loadTicketsForUid,
  };
};
